import React, { useState } from 'react';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Stat, StatRow } from '/imports/ui/Stat';
import { ChipGroup } from '/imports/ui/ChipGroup';
import { Badge, BadgeTone } from '/imports/ui/Badge';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useMethod, useUser } from '/imports/hooks';
import { getBotHealth } from '/imports/api/analytics';

const WINDOWS = [
  { id: 7, label: '7d' },
  { id: 30, label: '30d' },
  { id: 90, label: '90d' },
];

const int = (n: number) => n.toLocaleString();
const tix = (n: number) => (Math.round(n * 100) / 100).toLocaleString();

const statusTone: Record<string, BadgeTone> = {
  running: 'green', inTrade: 'blue', connecting: 'amber', loggingIn: 'amber',
  restarting: 'amber', stalled: 'gold', crashed: 'red', offline: 'neutral', unknown: 'neutral',
};

export const BotHealthPage = () => {
  const user = useUser();
  const [days, setDays] = useState(30);
  const [onlyTrouble, setOnlyTrouble] = useState(false);
  const [health, loading] = useMethod(getBotHealth, { days }, [days]);

  if (!user?.isSuperUser) return <NotPermittedPage />;

  const rows = (health?.rows ?? []).filter((r) => !onlyTrouble || r.crashes + r.stalls + r.restarts > 0);
  const troubled = (health?.rows ?? []).filter((r) => r.crashes + r.stalls > 0).length;
  const running = health?.byStatus.find((s) => s.status === 'running')?.count ?? 0;
  const offline = (health?.byStatus.find((s) => s.status === 'offline')?.count ?? 0)
    + (health?.byStatus.find((s) => s.status === 'crashed')?.count ?? 0);

  return (
    <Page>
      <PageHeading
        left={<h1>Bot Health</h1>}
        right={<ChipGroup ariaLabel="Window" chips={WINDOWS} value={days} onChange={(id) => setDays(id as number)} />}
      />
      <PageBody>
        {loading || !health ? <LoadingFlex /> : (
          <div className="flex flex-col gap-3 pb-6">
            <StatRow>
              <Stat label="Fleet" value={int(health.rows.length)} icon="construction" hint={`${int(health.rows.filter((r) => r.active).length)} active`} />
              <Stat label="Running" value={int(running)} icon="circleCheck" hint="reported by the bot itself" />
              <Stat label="Offline or crashed" value={int(offline)} icon="warning" hint="not currently trading" />
              {/*
                Clicking filters the table. The only tile here where a drill-down
                is the obvious next question, so it is the only clickable one --
                a row of pseudo-buttons would just be noise.
              */}
              <Stat
                label="Bots with incidents"
                value={int(troubled)}
                icon="block"
                hint={onlyTrouble ? 'showing only these' : `crashed or stalled in ${health.windowDays}d`}
                info="Click to filter the table to bots with recorded incidents"
                onClick={() => setOnlyTrouble((v) => !v)}
              />
            </StatRow>

            {/*
              Both logs only fill up as the fleet runs, so an empty column here
              means "not recorded yet" rather than "nothing happened". Saying
              which is which stops a wall of zeros reading as a clean bill of health.
            */}
            {(!health.hasStatusEvents || !health.hasSnapshots) && (
              <Panel className="border-warn/40">
                <div className="flex flex-col gap-1 text-[13px] text-fg-2">
                  {!health.hasStatusEvents && (
                    <div><Badge tone="amber">No status history</Badge> <span className="ml-1">Nothing has been recorded in this window, so incident columns read zero rather than healthy.</span></div>
                  )}
                  {!health.hasSnapshots && (
                    <div><Badge tone="amber">No inventory snapshot</Badge> <span className="ml-1">The nightly roll-up has not run, so stock columns are blank rather than empty.</span></div>
                  )}
                </div>
              </Panel>
            )}

            <Panel
              eyebrow="Fleet"
              title="Every bot"
              actions={
                <span className="flex items-center gap-3 text-[12px] text-fg-3">
                  <span>incidents over {health.windowDays}d</span>
                  {onlyTrouble && (
                    <button type="button" onClick={() => setOnlyTrouble(false)} className="cursor-pointer rounded border-none bg-transparent px-1 text-fg-3 hover:text-fg-1">
                      show all
                    </button>
                  )}
                </span>
              }
            >
              {rows.length === 0 ? (
                <div className="py-12 text-center text-[13px] text-fg-3">
                  {onlyTrouble ? 'No bot has recorded an incident in this window' : 'No bots configured'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[760px]">
                    <div className="eyebrow grid grid-cols-[1.3fr_auto_auto_repeat(6,minmax(0,1fr))] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Bot</span>
                      <span>Mode</span>
                      <span>Status</span>
                      <span className="text-right">Crash</span>
                      <span className="text-right">Stall</span>
                      <span className="text-right">Restart</span>
                      <span className="text-right">Stock</span>
                      <span className="text-right">At cost</span>
                      <span className="text-right">Bought</span>
                    </div>
                    {rows.map((r) => (
                      <div key={r.bot} className="grid grid-cols-[1.3fr_auto_auto_repeat(6,minmax(0,1fr))] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <span className={`truncate ${r.active ? 'text-fg-1' : 'text-fg-3'}`} title={r.active ? undefined : 'inactive'}>{r.bot}</span>
                        <span className="text-[12px] text-fg-3">{r.mode}</span>
                        <span><Badge tone={statusTone[r.status] || 'neutral'}>{r.status}</Badge></span>
                        <span className={`text-right font-mono tabular-nums ${r.crashes ? 'text-sell font-semibold' : 'text-fg-3'}`}>{r.crashes || '·'}</span>
                        <span className={`text-right font-mono tabular-nums ${r.stalls ? 'text-warn font-semibold' : 'text-fg-3'}`}>{r.stalls || '·'}</span>
                        <span className={`text-right font-mono tabular-nums ${r.restarts ? 'text-fg-1' : 'text-fg-3'}`}>{r.restarts || '·'}</span>
                        {/*
                          Null and zero are different: null means no snapshot has
                          been taken, zero means the bot genuinely holds nothing.
                        */}
                        <span className="text-right font-mono tabular-nums text-fg-2">{r.units == null ? '--' : int(r.units)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{r.valueAtBuy == null ? '--' : tix(r.valueAtBuy)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{r.unitsAcquired ? int(r.unitsAcquired) : '·'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Panel>
          </div>
        )}
      </PageBody>
    </Page>
  );
};
